/**
 * L'ANNEAU D'AVANCEMENT D'UN PROJET : un MOTEUR CUSTOM en JavaScript vanilla,
 * monte dans la vue « Fiche résumée » par `useMoteurVanilla`
 * (`docs/SURFACES-CANVAS.md`, section 5, chantier `fiche-suivi`).
 *
 * POURQUOI UN MOTEUR ET PAS UN COMPOSANT. C'est la preuve que la vue de fiche
 * Canvas accueille ce que les portes Canvas accueillent deja : une bibliotheque
 * qui prend un element du DOM et s'y installe (un graphe, une carte, un
 * tableur). Celui-ci est minuscule a dessein : il dessine un anneau SVG et sait
 * changer de valeur EN PLACE, sans etre remonte (`poser`), ce que la vue fait
 * quand l'avancement change sous ses yeux.
 *
 * LES COULEURS SONT DES CLASSES DU DESIGN SYSTEM (`stroke-accent`,
 * `stroke-surface-3`, `fill-ink-900`) : Tailwind les genere parce que ce
 * fichier est sous `src/`, et le theme de l'org les teinte. Les longueurs sont
 * des unites de la `viewBox`, jamais des pixels : l'anneau prend la taille de
 * son hote.
 *
 * AUCUN IMPORT : le moteur ne connait ni React, ni le socle, ni la fiche. Il
 * recoit un nombre (0 a 100, ou `null` quand l'avancement n'est pas renseigne)
 * et son libelle accessible.
 */

const SVG = 'http://www.w3.org/2000/svg';
/** Le rayon et l'epaisseur, en unites de la viewBox (0 0 40 40). */
const RAYON = 16;
const EPAISSEUR = 4;
const CIRCONFERENCE = 2 * Math.PI * RAYON;

export interface AnneauDAvancement {
  /** Change la valeur sans remonter le dessin (la transition le fait glisser). */
  poser(valeur: number | null, libelle: string): void;
  /** Retire le dessin de son hote. */
  detruire(): void;
}

/** Une valeur bornee a 0..100, arrondie ; `null` reste `null`. */
export function borner(valeur: number | null): number | null {
  if (valeur === null || !Number.isFinite(valeur)) return null;
  return Math.round(Math.min(100, Math.max(0, valeur)));
}

/** Le decalage du trait qui dessine `valeur` pour cent de la circonference. */
export function decalagePour(valeur: number | null): number {
  const borne = borner(valeur) ?? 0;
  return CIRCONFERENCE * (1 - borne / 100);
}

function cercle(document: Document, classes: string): SVGCircleElement {
  const element = document.createElementNS(SVG, 'circle');
  element.setAttribute('cx', '20');
  element.setAttribute('cy', '20');
  element.setAttribute('r', String(RAYON));
  element.setAttribute('fill', 'none');
  element.setAttribute('stroke-width', String(EPAISSEUR));
  element.setAttribute('class', classes);
  return element;
}

/** Installe l'anneau dans `hote` et rend de quoi le piloter. */
export function monterAnneau(hote: HTMLElement, valeur: number | null, libelle: string): AnneauDAvancement {
  const document = hote.ownerDocument;
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 40 40');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('role', 'img');
  svg.dataset['moteur'] = 'anneau-d-avancement';

  const piste = cercle(document, 'stroke-surface-3');
  const trait = cercle(document, 'stroke-accent transition-[stroke-dashoffset] duration-500 ease-out');
  trait.setAttribute('stroke-linecap', 'round');
  trait.setAttribute('stroke-dasharray', String(CIRCONFERENCE));
  /* Le trait part de midi, dans le sens des aiguilles d'une montre. */
  trait.setAttribute('transform', 'rotate(-90 20 20)');

  const chiffre = document.createElementNS(SVG, 'text');
  chiffre.setAttribute('x', '20');
  chiffre.setAttribute('y', '20');
  chiffre.setAttribute('text-anchor', 'middle');
  chiffre.setAttribute('dominant-baseline', 'central');
  chiffre.setAttribute('font-size', '9');
  chiffre.setAttribute('font-weight', '600');
  chiffre.setAttribute('class', 'fill-ink-900');

  svg.append(piste, trait, chiffre);
  hote.appendChild(svg);

  const anneau: AnneauDAvancement = {
    poser(nouvelle, nouveauLibelle) {
      const borne = borner(nouvelle);
      trait.setAttribute('stroke-dashoffset', String(decalagePour(borne)));
      trait.setAttribute('visibility', borne === null || borne === 0 ? 'hidden' : 'visible');
      chiffre.textContent = borne === null ? '-' : `${borne} %`;
      svg.setAttribute('aria-label', borne === null ? `${nouveauLibelle} : non renseigné` : `${nouveauLibelle} : ${borne} %`);
      svg.dataset['valeur'] = borne === null ? '' : String(borne);
    },
    detruire() {
      svg.remove();
    },
  };
  anneau.poser(valeur, libelle);
  return anneau;
}
